import React, { useState } from 'react';
import { Link, useNavigate, useParams } from '@tanstack/react-router';
import { useQuery } from '@tanstack/react-query';
import { Alert, Button } from '@mailflow/ui';
import { api } from '../lib/api.js';
import {
  campaignQuery,
  campaignRecipientsQuery,
  useCampaignAction,
  useScheduleCampaign,
  useAutoResendCampaign,
  useDuplicateCampaign,
  useDeleteCampaign,
  type Versandlage,
  type CampaignVariant,
} from '../lib/campaigns.js';
import { CampaignAnalyticsDashboard } from '../components/CampaignAnalyticsDashboard.js';
import { SuppressContactDialog } from '../components/SuppressContactDialog.js';
import { CreateDealModal } from '../components/CreateDealModal.js';

const SKIP_LABELS: Record<string, string> = {
  suppressed: 'gesperrt',
  unsubscribed: 'abgemeldet',
  bounced: 'unzustellbar',
  frequency_cap: 'Frequenzgrenze',
  invalid: 'ungültige Adresse',
  no_consent: 'keine Einwilligung',
  duplicate: 'abgebrochen',
};

export function CampaignDetailPage() {
  const params = useParams({ strict: false }) as { id?: string };
  const fallbackId = typeof window !== 'undefined' ? window.location.pathname.split('/').filter(Boolean).pop() : '';
  const id = params?.id || (fallbackId && fallbackId !== 'kampagnen' ? fallbackId : '');

  const navigate = useNavigate();
  const { data, isLoading, isError, error } = useQuery(campaignQuery(id));
  const action = useCampaignAction(id);
  const schedule = useScheduleCampaign(id);
  const duplicate = useDuplicateCampaign();
  const remove = useDeleteCampaign();

  const [showAutoResend, setShowAutoResend] = useState(false);
  const [showSchedule, setShowSchedule] = useState(false);
  const [showEmailModal, setShowEmailModal] = useState(false);
  const [scheduleAt, setScheduleAt] = useState('');

  async function handleDuplicate() {
    if (!id) return;
    const res = await duplicate.mutateAsync(id);
    await navigate({ to: '/kampagnen/$id', params: { id: res.campaign.id } });
  }

  async function handleDelete() {
    if (!id) return;
    if (confirm(`Möchtest Du die Kampagne "${data?.campaign?.name ?? ''}" wirklich löschen?`)) {
      await remove.mutateAsync(id);
      await navigate({ to: '/kampagnen' });
    }
  }

  if (isLoading) return <p className="muted" style={{ padding: 'var(--space-4)' }}>Wird geladen …</p>;
  if (isError || !data?.campaign || !id) {
    return (
      <div className="stack" style={{ gap: 'var(--space-4)', padding: 'var(--space-4)' }}>
        <nav className="breadcrumb text-sm">
          <Link to="/kampagnen">← Zurück zu allen Kampagnen</Link>
        </nav>
        <div className="alert" data-tone="danger" role="alert">
          {error instanceof Error ? error.message : 'Kampagne konnte nicht geladen werden.'}
        </div>
      </div>
    );
  }

  const campaign = data.campaign;
  const variants = data.variants ?? [];
  const stats = data.stats ?? {
    total: 0,
    queued: 0,
    sending: 0,
    sent: 0,
    failed: 0,
    skipped: 0,
    uniqueOpens: 0,
    uniqueClicks: 0,
    machineOpens: 0,
    unsubscribes: 0,
    bounces: 0,
    openRate: null,
    clickRate: null,
    skipReasons: {},
    linkStats: [],
  };
  const versandlage = data.versandlage ?? {
    grund: null,
    abhilfe: null,
    aktiveAbsender: 0,
    faellig: 0,
    spaeter: 0,
    zurueckgehalten: 0,
    naechsteFaelligkeit: null,
    letzterVersand: null,
  };

  const running = campaign.status === 'sending';

  return (
    <div className="stack" style={{ gap: 'var(--space-6)' }}>
      <nav className="breadcrumb text-sm">
        <Link to="/kampagnen">Kampagnen</Link>
        <span aria-hidden="true"> / </span>
        <span className="muted">{campaign.name}</span>
      </nav>

      <header className="page-header">
        <div>
          <h1 className="text-2xl">{campaign.name}</h1>
          <p className="muted text-sm">
            {campaign.fromName} &lt;{campaign.fromEmail}&gt;
          </p>
        </div>
        <div className="cluster">
          {(campaign.status === 'draft' || campaign.status === 'scheduled') && (
            <Link to="/kampagnen/$id/bearbeiten" params={{ id: campaign.id }} className="btn" data-variant="secondary">
              ✏️ Bearbeiten
            </Link>
          )}
          {campaign.status === 'draft' && (
            <>
              <Button onClick={() => action.mutate('start')} loading={action.isPending}>
                Versand starten
              </Button>
              <Button variant="secondary" onClick={() => setShowSchedule(true)}>
                Planen
              </Button>
            </>
          )}
          {(running || campaign.status === 'scheduled') && (
            <Button variant="secondary" onClick={() => action.mutate('pause')}>
              Pausieren
            </Button>
          )}
          {campaign.status === 'paused' && (
            <Button onClick={() => action.mutate('resume')}>Fortsetzen</Button>
          )}
          {(running || campaign.status === 'scheduled' || campaign.status === 'paused') && (
            <Button variant="danger" onClick={() => action.mutate('cancel')}>
              Abbrechen
            </Button>
          )}
          {campaign.status === 'completed' && (
            <Button variant="secondary" onClick={() => setShowAutoResend(true)}>
              ⏱️ Auto-Resend an Nicht-Öffner
            </Button>
          )}
          <Button variant="secondary" onClick={handleDuplicate} loading={duplicate.isPending}>
            📋 Duplizieren
          </Button>
          <Button variant="danger" onClick={handleDelete} loading={remove.isPending}>
            🗑️ Löschen
          </Button>
        </div>
      </header>

      {action.isError && (
        <div className="alert" data-tone="danger" role="alert">
          {action.error instanceof Error ? action.error.message : 'Aktion fehlgeschlagen.'}
        </div>
      )}

      {schedule.isError && (
        <div className="alert" data-tone="danger" role="alert">
          {schedule.error instanceof Error ? schedule.error.message : 'Planung fehlgeschlagen.'}
        </div>
      )}

      {showSchedule && (
        <section className="card" aria-label="Kampagne planen">
          <div className="card__body stack">
            <h2 className="text-lg">Versand planen</h2>
            <label className="stack" style={{ gap: 'var(--space-1)' }}>
              <span className="text-sm">Zeitpunkt (lokale Browserzeit)</span>
              <input
                className="input"
                type="datetime-local"
                value={scheduleAt}
                onChange={(event) => setScheduleAt(event.target.value)}
              />
            </label>
            <div style={{ display: 'flex', gap: 'var(--space-2)' }}>
              <Button
                onClick={() => {
                  if (!scheduleAt) return;
                  schedule.mutate(new Date(scheduleAt).toISOString(), {
                    onSuccess: () => setShowSchedule(false),
                  });
                }}
                loading={schedule.isPending}
                disabled={!scheduleAt}
              >
                Planung speichern
              </Button>
              <Button variant="ghost" onClick={() => setShowSchedule(false)}>
                Schließen
              </Button>
            </div>
          </div>
        </section>
      )}

      <Versandhinweis lage={versandlage} />

      <CampaignAnalyticsDashboard stats={stats} />

      {/* Link-Klick Heatmap Card */}
      {stats.linkStats && stats.linkStats.length > 0 && (
        <section className="card">
          <div className="card__body stack">
            <h2 style={{ fontSize: 'var(--text-lg)' }}>🎯 Link-Klick Heatmap &amp; Analytik</h2>
            <div className="stack" style={{ gap: 'var(--space-2)' }}>
              {stats.linkStats.map((link) => {
                const totalClicks = stats.uniqueClicks || 1;
                const percentage = Math.round((link.uniqueClicks / totalClicks) * 100);
                return (
                  <div
                    key={link.url}
                    style={{
                      padding: '8px 12px',
                      background: 'var(--color-surface-hover)',
                      borderRadius: '6px',
                    }}
                  >
                    <div
                      style={{
                        display: 'flex',
                        justifyContent: 'space-between',
                        marginBottom: '4px',
                      }}
                    >
                      <a
                        href={link.url}
                        target="_blank"
                        rel="noopener noreferrer"
                        style={{ fontWeight: 600, wordBreak: 'break-all' }}
                      >
                        {link.url}
                      </a>
                      <span>
                        <strong>{link.uniqueClicks}</strong> klicks ({percentage}%)
                      </span>
                    </div>
                    <div
                      style={{
                        width: '100%',
                        background: 'var(--color-border)',
                        height: '6px',
                        borderRadius: '3px',
                        overflow: 'hidden',
                      }}
                    >
                      <div
                        style={{
                          width: `${percentage}%`,
                          background: 'var(--color-accent)',
                          height: '100%',
                        }}
                      />
                    </div>
                  </div>
                );
              })}
            </div>
          </div>
        </section>
      )}

      {Object.keys(stats.skipReasons ?? {}).length > 0 && (
        <section className="card">
          <div className="card__body stack">
            <h2 style={{ fontSize: 'var(--text-lg)' }}>Nicht versendet</h2>
            <ul className="reason-list">
              {Object.entries(stats.skipReasons).map(([reason, count]) => (
                <li key={reason}>
                  <strong data-numeric>{count}</strong> {SKIP_LABELS[reason] ?? reason}
                </li>
              ))}
            </ul>
          </div>
        </section>
      )}

      <section className="card">
        <div className="card__body stack">
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
            <h2 style={{ fontSize: 'var(--text-lg)' }}>
              {variants.length > 1 ? 'Varianten (A/B-Test)' : 'Versendeter E-Mail-Inhalt'}
            </h2>
            {variants[0] && (
              <Button variant="secondary" onClick={() => setShowEmailModal(true)}>
                👁️ Vollständige E-Mail anzeigen
              </Button>
            )}
          </div>

          {variants.map((variant) => (
            <div key={variant.id} className="variant" style={{ background: 'var(--color-surface-hover)', padding: '12px 16px', borderRadius: '8px' }}>
              <div className="variant__head" style={{ display: 'flex', justifyContent: 'space-between', marginBottom: '6px' }}>
                <strong>{variants.length > 1 ? `Variante ${variant.key}` : 'Betreff'}</strong>
                {variant.isWinner && (
                  <span className="badge" data-status="active">
                    Gewinner
                  </span>
                )}
              </div>
              <p style={{ margin: 0, fontWeight: 600, fontSize: '1.05rem' }}>{variant.subject}</p>
              <div className="muted text-sm" style={{ marginTop: '4px' }}>
                Versendet von {campaign.fromName} &lt;{campaign.fromEmail}&gt;
                {campaign.startedAt ? ` am ${new Date(campaign.startedAt).toLocaleString('de-CH')}` : ''}
              </div>
            </div>
          ))}
        </div>
      </section>

      <CampaignRecipientList campaignId={id} campaignName={campaign.name} />

      {showEmailModal && variants[0] && (
        <ViewEmailModal
          variant={variants[0]}
          campaignName={campaign.name}
          fromName={campaign.fromName}
          fromEmail={campaign.fromEmail}
          sentAt={campaign.startedAt}
          onClose={() => setShowEmailModal(false)}
        />
      )}

      {showAutoResend && (
        <AutoResendModal
          campaignId={id}
          origSubject={variants[0]?.subject ?? campaign.name}
          onClose={() => setShowAutoResend(false)}
        />
      )}
    </div>
  );
}

function Versandhinweis({ lage }: { lage?: Versandlage | null }) {
  if (!lage || !lage.grund) return null;

  const zeit = (wert: string | null) =>
    wert ? new Date(wert).toLocaleString('de-CH', { dateStyle: 'short', timeStyle: 'short' }) : null;

  const naechste = zeit(lage.naechsteFaelligkeit);

  return (
    <Alert tone="warning" title={lage.grund}>
      {lage.abhilfe && <p>{lage.abhilfe}</p>}
      <p className="muted" style={{ marginTop: 'var(--space-1)' }}>
        {lage.faellig} fällig · {lage.spaeter} für später eingeplant
        {lage.zurueckgehalten > 0 && ` · ${lage.zurueckgehalten} warten auf den A/B-Gewinner`}
        {naechste && ` · nächste ab ${naechste}`}
      </p>
    </Alert>
  );
}

const FILTER_TABS: Array<{ id: string; label: string }> = [
  { id: 'alle', label: 'Alle' },
  { id: 'geoeffnet', label: 'Geöffnet' },
  { id: 'nicht_geoeffnet', label: 'Nicht geöffnet' },
  { id: 'geklickt', label: 'Geklickt' },
  { id: 'nicht_geklickt', label: 'Nicht geklickt' },
  { id: 'gebounct', label: 'Gebounct' },
  { id: 'uebersprungen', label: 'Übersprungen' },
];

function ViewEmailModal({
  variant,
  campaignName,
  fromName,
  fromEmail,
  sentAt,
  onClose,
}: {
  variant: CampaignVariant;
  campaignName: string;
  fromName: string;
  fromEmail: string;
  sentAt?: string | null;
  onClose: () => void;
}) {
  const [tab, setTab] = useState<'html' | 'text'>('html');

  return (
    <div className="overlay" onMouseDown={(e) => e.target === e.currentTarget && onClose()}>
      <div className="card dialog" style={{ maxWidth: '800px', width: '90vw' }} role="dialog">
        <div className="card__body stack">
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
            <h2 style={{ fontSize: 'var(--text-lg)' }}>E-Mail-Vorschau: {campaignName}</h2>
            <Button variant="ghost" onClick={onClose}>✕ Schließen</Button>
          </div>

          <div style={{ padding: '12px', background: 'var(--color-surface-hover)', borderRadius: '8px' }}>
            <div><strong>Betreff:</strong> {variant.subject}</div>
            <div><strong>Absender:</strong> {fromName} &lt;{fromEmail}&gt;</div>
            {sentAt && <div><strong>Gesendet am:</strong> {new Date(sentAt).toLocaleString('de-CH')}</div>}
          </div>

          <div className="tabs" style={{ display: 'flex', gap: '8px' }}>
            <button
              type="button"
              className={`button ${tab === 'html' ? '' : 'button--secondary'}`}
              onClick={() => setTab('html')}
            >
              HTML Vorschau
            </button>
            <button
              type="button"
              className={`button ${tab === 'text' ? '' : 'button--secondary'}`}
              onClick={() => setTab('text')}
            >
              Text Version
            </button>
          </div>

          {tab === 'html' ? (
            <div style={{ border: '1px solid var(--color-border)', borderRadius: '8px', overflow: 'hidden', minHeight: '300px' }}>
              <iframe
                title="E-Mail Vorschau"
                srcDoc={variant.html}
                style={{ width: '100%', height: '400px', border: 'none', background: '#fff' }}
                sandbox="allow-same-origin"
              />
            </div>
          ) : (
            <pre
              style={{
                padding: '12px',
                background: 'var(--color-surface)',
                border: '1px solid var(--color-border)',
                borderRadius: '8px',
                whiteSpace: 'pre-wrap',
                fontFamily: 'monospace',
                fontSize: '0.85rem',
                maxHeight: '400px',
                overflowY: 'auto',
              }}
            >
              {variant.html.replace(/<[^>]*>/g, '')}
            </pre>
          )}
        </div>
      </div>
    </div>
  );
}

function CampaignRecipientList({
  campaignId,
  campaignName = 'Kampagne',
}: {
  campaignId: string;
  campaignName?: string;
}) {
  const [filter, setFilter] = useState('alle');
  const [creatingSegment, setCreatingSegment] = useState(false);
  const [selectedContactIds, setSelectedContactIds] = useState<string[]>([]);
  const [suppressTarget, setSuppressTarget] = useState<{ id: string; email: string } | null>(null);
  const [dealTargetContactIds, setDealTargetContactIds] = useState<string[] | null>(null);

  const navigate = useNavigate();
  const recipientsQuery = useQuery(campaignRecipientsQuery(campaignId, { filter }));

  const items = recipientsQuery.data?.items ?? [];

  const handleSelectAll = (checked: boolean) => {
    if (checked) {
      setSelectedContactIds(items.map((i) => i.contactId));
    } else {
      setSelectedContactIds([]);
    }
  };

  const handleSelectOne = (contactId: string, checked: boolean) => {
    if (checked) {
      setSelectedContactIds((prev) => [...prev, contactId]);
    } else {
      setSelectedContactIds((prev) => prev.filter((id) => id !== contactId));
    }
  };

  const handleCreateSegment = async () => {
    try {
      setCreatingSegment(true);
      const tabLabel = FILTER_TABS.find((t) => t.id === filter)?.label ?? filter;
      const name = `${campaignName} - ${tabLabel}`;

      let definition: any = { type: 'email_event', event: 'sent', scope: { campaignId } };
      if (filter === 'geoeffnet') {
        definition = { type: 'email_event', event: 'open', scope: { campaignId } };
      } else if (filter === 'nicht_geoeffnet') {
        definition = {
          op: 'AND',
          children: [
            { type: 'email_event', event: 'sent', scope: { campaignId } },
            { type: 'email_event', event: 'open', scope: { campaignId }, negate: true },
          ],
        };
      } else if (filter === 'geklickt') {
        definition = { type: 'email_event', event: 'click', scope: { campaignId } };
      } else if (filter === 'nicht_geklickt') {
        definition = {
          op: 'AND',
          children: [
            { type: 'email_event', event: 'sent', scope: { campaignId } },
            { type: 'email_event', event: 'click', scope: { campaignId }, negate: true },
          ],
        };
      } else if (filter === 'gebounct') {
        definition = { type: 'email_event', event: 'bounce_hard', scope: { campaignId } };
      } else if (filter === 'uebersprungen') {
        definition = {
          op: 'AND',
          children: [
            { type: 'email_event', event: 'sent', scope: { campaignId }, negate: true },
            { type: 'attribute', field: 'status', op: 'equals', value: 'suppressed' },
          ],
        };
      }

      const res = await api.post<{ segment: { id: string } }>('/api/segments', {
        name,
        definition,
        mode: 'live',
      });

      await navigate({
        to: '/kampagnen/neu',
        search: { segmentId: res.segment.id } as any,
      });
    } catch (err) {
      alert(err instanceof Error ? err.message : 'Segment konnte nicht erstellt werden.');
    } finally {
      setCreatingSegment(false);
    }
  };

  return (
    <section className="card">
      <div className="card__body stack">
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap', gap: '12px' }}>
          <h2 style={{ fontSize: 'var(--text-lg)' }}>Empfängerliste ({items.length})</h2>
          <Button
            variant="secondary"
            onClick={() => void handleCreateSegment()}
            disabled={items.length === 0}
            loading={creatingSegment}
          >
            Segment erstellen &amp; neue Kampagne starten ↗
          </Button>
        </div>

        {/* BULK ACTIONS TOOLBAR */}
        {selectedContactIds.length > 0 && (
          <div
            style={{
              padding: '10px 16px',
              background: 'var(--color-surface-hover)',
              borderRadius: '8px',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'space-between',
              border: '1px solid var(--color-border)',
            }}
          >
            <span style={{ fontWeight: 600, fontSize: '0.9rem' }}>
              {selectedContactIds.length} Empfänger ausgewählt
            </span>
            <div className="cluster" style={{ gap: '8px' }}>
              <Button
                variant="secondary"
                onClick={() => {
                  const firstSelected = items.find((i) => selectedContactIds.includes(i.contactId));
                  if (firstSelected) {
                    setSuppressTarget({ id: firstSelected.contactId, email: firstSelected.email });
                  }
                }}
              >
                🚫 Ausgewählte sperren
              </Button>
              <Button
                onClick={() => {
                  setDealTargetContactIds(selectedContactIds);
                }}
              >
                💼 Rezensionsfall / Won anlegen
              </Button>
            </div>
          </div>
        )}

        <div className="tabs" style={{ display: 'flex', gap: 'var(--space-2)', flexWrap: 'wrap' }}>
          {FILTER_TABS.map((tab) => (
            <button
              key={tab.id}
              type="button"
              className={`button ${filter === tab.id ? '' : 'button--secondary'}`}
              style={{ padding: '4px 12px', fontSize: 'var(--text-sm)' }}
              onClick={() => {
                setFilter(tab.id);
                setSelectedContactIds([]);
              }}
            >
              {tab.label}
            </button>
          ))}
        </div>

        {recipientsQuery.isLoading ? (
          <p className="muted">Lade Empfänger …</p>
        ) : items.length === 0 ? (
          <p className="muted">Keine Empfänger für diesen Filter vorhanden.</p>
        ) : (
          <div style={{ overflowX: 'auto' }}>
            <table className="table">
              <thead>
                <tr>
                  <th style={{ width: '40px' }}>
                    <input
                      type="checkbox"
                      checked={items.length > 0 && selectedContactIds.length === items.length}
                      onChange={(e) => handleSelectAll(e.target.checked)}
                      aria-label="Alle auswählen"
                    />
                  </th>
                  <th>E-Mail &amp; Name</th>
                  <th>Status &amp; Rezensionsfall</th>
                  <th>Gesendet</th>
                  <th>Geöffnet</th>
                  <th>Geklickt</th>
                  <th style={{ textAlign: 'right' }}>Aktionen</th>
                </tr>
              </thead>
              <tbody>
                {items.map((r) => {
                  const isChecked = selectedContactIds.includes(r.contactId);
                  const isSuppressedRow = r.isSuppressed || r.contactStatus === 'suppressed';
                  const isWonDeal = r.dealStatus === 'won';
                  const isOpenDeal = r.dealStatus === 'open';

                  // Row highlight background
                  let rowStyle: React.CSSProperties = {};
                  if (isSuppressedRow) {
                    rowStyle = { background: 'rgba(239, 68, 68, 0.12)', borderLeft: '4px solid #ef4444' };
                  } else if (isWonDeal) {
                    rowStyle = { background: 'rgba(16, 185, 129, 0.12)', borderLeft: '4px solid #10b981' };
                  } else if (isOpenDeal) {
                    rowStyle = { background: 'rgba(245, 158, 11, 0.12)', borderLeft: '4px solid #f59e0b' };
                  }

                  return (
                    <tr key={r.messageId} style={rowStyle}>
                      <td>
                        <input
                          type="checkbox"
                          checked={isChecked}
                          onChange={(e) => handleSelectOne(r.contactId, e.target.checked)}
                          aria-label={`Kontakt ${r.email} auswählen`}
                        />
                      </td>
                      <td>
                        <div>
                          <Link to="/kontakte/$id" params={{ id: r.contactId }} style={{ fontWeight: 600 }}>
                            {r.email}
                          </Link>
                          {[r.firstName, r.lastName].filter(Boolean).join(' ') && (
                            <div className="muted text-xs">
                              {[r.firstName, r.lastName].filter(Boolean).join(' ')}
                            </div>
                          )}
                        </div>
                      </td>
                      <td>
                        <div className="stack" style={{ gap: '4px' }}>
                          {/* Nachrichten Status Badge */}
                          <span
                            className="badge"
                            data-status={
                              r.status === 'sent'
                                ? 'active'
                                : r.status === 'failed'
                                ? 'danger'
                                : 'paused'
                            }
                          >
                            {r.status === 'sent'
                              ? 'Gesendet'
                              : r.status === 'skipped'
                              ? (SKIP_LABELS[r.skipReason ?? ''] ?? r.skipReason ?? 'Übersprungen')
                              : r.status === 'queued'
                              ? `Warteschlange ${
                                  r.scheduledFor
                                    ? `(Versuch ${new Date(r.scheduledFor).toLocaleTimeString('de-DE', { hour: '2-digit', minute: '2-digit' })})`
                                    : ''
                                }`
                              : r.status}
                          </span>

                          {/* Sperrlisten-Badge (Rot) */}
                          {isSuppressedRow && (
                            <span className="badge" style={{ background: '#ef4444', color: '#fff', fontSize: '0.75rem' }}>
                              🚫 Auf Sperrliste
                            </span>
                          )}

                          {/* Auftrags / Rezensionsfall Badge (Grün = Won, Gelb/Orange = Open) */}
                          {isWonDeal && (
                            <span className="badge" style={{ background: '#10b981', color: '#fff', fontSize: '0.75rem' }}>
                              🟢 Rezensionsfall gelöst {r.dealTitle ? `(${r.dealTitle})` : ''}
                            </span>
                          )}
                          {isOpenDeal && (
                            <span className="badge" style={{ background: '#f59e0b', color: '#fff', fontSize: '0.75rem' }}>
                              🟠 In Bearbeitung {r.dealTitle ? `(${r.dealTitle})` : ''}
                            </span>
                          )}

                          {r.errorMessage && (
                            <div
                              style={{
                                fontSize: '0.75rem',
                                color: '#f87171',
                                background: 'rgba(239, 68, 68, 0.1)',
                                padding: '2px 6px',
                                borderRadius: '4px',
                                maxWidth: '300px',
                                wordBreak: 'break-word',
                              }}
                            >
                              ⚠️ {r.errorMessage}
                            </div>
                          )}
                        </div>
                      </td>
                      <td>{r.sentAt ? new Date(r.sentAt).toLocaleString('de-CH') : '—'}</td>
                      <td>{r.openedAt ? new Date(r.openedAt).toLocaleString('de-CH') : '—'}</td>
                      <td>{r.clickedAt ? new Date(r.clickedAt).toLocaleString('de-CH') : '—'}</td>
                      <td style={{ textAlign: 'right' }}>
                        <div className="cluster" style={{ justifyContent: 'flex-end', gap: '4px' }}>
                          <Button
                            variant="ghost"
                            onClick={() => setSuppressTarget({ id: r.contactId, email: r.email })}
                            title="Auf Sperrliste setzen"
                          >
                            🚫 Sperren
                          </Button>
                          <Button
                            variant="ghost"
                            onClick={() => setDealTargetContactIds([r.contactId])}
                            title="Rezensionsfall anlegen"
                          >
                            💼 Rezensionsfall
                          </Button>
                        </div>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        )}

        {/* MODAL DIALOGS */}
        {suppressTarget && (
          <SuppressContactDialog
            contactId={suppressTarget.id}
            email={suppressTarget.email}
            onClose={() => setSuppressTarget(null)}
            onSuccess={() => void recipientsQuery.refetch()}
          />
        )}

        {dealTargetContactIds && dealTargetContactIds.length > 0 && (
          <CreateDealModal
            contactIds={dealTargetContactIds}
            contactEmail={
              dealTargetContactIds.length === 1
                ? items.find((i) => i.contactId === dealTargetContactIds[0])?.email
                : undefined
            }
            onClose={() => setDealTargetContactIds(null)}
            onSuccess={() => void recipientsQuery.refetch()}
          />
        )}
      </div>
    </section>
  );
}

function AutoResendModal({
  campaignId,
  origSubject,
  onClose,
}: {
  campaignId: string;
  origSubject: string;
  onClose: () => void;
}) {
  const navigate = useNavigate();
  const [newSubject, setNewSubject] = useState(`Erinnerung: ${origSubject}`);
  const autoResend = useAutoResendCampaign(campaignId);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!newSubject.trim()) return;
    const res = await autoResend.mutateAsync(newSubject.trim());
    onClose();
    void navigate({ to: '/kampagnen/$id', params: { id: res.campaign.id } });
  };

  return (
    <div className="overlay" onMouseDown={(e) => e.target === e.currentTarget && onClose()}>
      <div
        className="card dialog"
        role="dialog"
        aria-modal="true"
        aria-label="Auto-Resend an Nicht-Öffner"
      >
        <div className="card__body stack">
          <h2 style={{ fontSize: 'var(--text-lg)' }}>
            ⏱️ Automatischer Nachversand an Nicht-Öffner
          </h2>
          <p className="text-sm muted">
            Erstellt eine Entwurfs-Kampagne mit identischem Inhalt, die gezielt an alle Empfänger
            adressiert ist, die die Haupt-Kampagne noch nicht geöffnet haben.
          </p>

          <form onSubmit={handleSubmit} className="stack">
            <div>
              <label className="label">Neuer Betreff für den Nachversand</label>
              <input
                className="input"
                value={newSubject}
                onChange={(e) => setNewSubject(e.target.value)}
                required
              />
            </div>
            <div className="dialog__actions" style={{ marginTop: '12px' }}>
              <Button variant="secondary" type="button" onClick={onClose}>
                Abbrechen
              </Button>
              <Button type="submit" loading={autoResend.isPending}>
                Nachversand anlegen ↗
              </Button>
            </div>
          </form>
        </div>
      </div>
    </div>
  );
}
